import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, Renderer2, ViewChild } from '@angular/core';
import { ITableResults } from '../../../../core/interfaces';
import { BulkContainersServiceErrorResolverService } from '../../core/services';
import { NotificationService } from '../../../../core/services';
import { ListComponentGeneric } from '../../../../ui/generics';
import { IBulkContainer } from '../../core/interfaces/IBulkContainer';
import { NonPackableService } from '../../core/services/NonPackableService';
import { INonPackableArticle } from '../../core/interfaces/INonPackableArticle';
import { Observable, Subject } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { SuppliersService } from '../../../suppliers/core/services/SuppliersService';
import { ISimpleListItem } from '../../../../core/interfaces/ISimpleListItem';
import { AddEditOrderModalComponent } from '../../orders/add-edit-order-modal/add-edit-order-modal.component';
import { OrderTypes } from '../../core/enums';
import { IDeliveryOrder } from '../../core/interfaces/IDeliveryOrder';
import { IDeliveryOrderProduct } from '../../core/interfaces/IDeliveryOrderProduct';

@Component({
    selector: 'app-non-packing-list',
    templateUrl: './non-packing-list.component.html',
    styleUrls: ['./non-packing-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NonPackingListComponent extends ListComponentGeneric<IBulkContainer> implements OnInit {

  @ViewChild('addEditOrderModal') addEditOrderModal: AddEditOrderModalComponent;

  public readonly orderTypes = OrderTypes;
  public isSuppliersLoading = false;
  public searchSupplierChange$ = new Subject();
  public selectedSuppliers: ISimpleListItem[] = [];
  public suppliersList: ISimpleListItem[];
  public showArticlesBelowMinStock = false;
  // @ts-ignore
  public data: ITableResults<INonPackableArticle> = {
    results: [],
    info: {
      page: 1,
      results: 0,
      totalResults: 0,
      exportTableName: null
    }
  };

  searchColumns = [
    'articleVariant.id',
    'articleVariant.orderNumber',
    'articleVariant.fullName',
    'nonPackableState',
    'orderDate',
    'supplier.name',
    'orderSupplier.name',
    'orderQuantitySum',
  ];

  protected readonly exportedTableVisibleName = 'Fremdmarke-Liste';

  constructor(
    protected _service: NonPackableService,
    protected _cdr: ChangeDetectorRef,
    protected _notifyService: NotificationService,
    protected renderer: Renderer2,
    protected _errorResolver: BulkContainersServiceErrorResolverService,
    private readonly _supplierService: SuppliersService
  ) {
    super(_service, _cdr, _notifyService, renderer, _errorResolver);
  }

  public ngOnInit(): void {
    super.ngOnInit();

    const getSuppliers = (name: string) =>
      this._supplierService.getSimpleList(name);

    const suppliersOptionList$: Observable<ISimpleListItem[]> = this.searchSupplierChange$
      .asObservable()
      .pipe(debounceTime(500))
      .pipe(switchMap(getSuppliers));
    suppliersOptionList$.subscribe(data => {
      this.suppliersList = [...data].sort((a, b) =>
        (a.value || '').localeCompare(b.value || '', 'de', { sensitivity: 'base' }));
      this.isSuppliersLoading = false;
      this._cdr.detectChanges();
    });

    this.onSearchSupplier('');
  }

  public onSearchSupplier(value: string): void {
    this.isSuppliersLoading = true;
    this.searchSupplierChange$.next(value);
  }

  load(
    pageIndex: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: string | null,
    filter: { key: string; value: string[] }[],
    search: { key: string; value: string[] }[],
    additionalFilters: { key: string; value: string[] }[] = [],
  ): void {
    filter = [
      ...filter,
      ...this.selectedSuppliers.map(x => ({
        key: 'supplier.name',
        value: [x.value]
      })),
    ];

    if (this.showArticlesBelowMinStock) {
      filter = [
        { key: 'isBelowMinStock', value: ['true'] },
        ...filter,
      ];
    }

    if (!sortField) {
      sortField = 'articleVariant.id';
    }

    if (!sortOrder) {
      sortOrder = 'descend';
    }

    this.lastLoadParams = {
      pageIndex: pageIndex,
      pageSize: pageSize,
      sortField: sortField,
      sortOrder: sortOrder,
      filter: filter,
      search: search,
      additionalFilters
    };

    this.persistTablePrefs();

    this.loading = true;

    this._service.getQueryableArticlesAll(pageIndex, pageSize, sortField, sortOrder, filter, search, additionalFilters).pipe(
    ).subscribe(res => {
      this.loading = false;
      this.data = res;
      this.refreshCheckedStatus();
      this._cdr.detectChanges();
    }, this.parseError);
  }

  public compareSimpleList(val1: ISimpleListItem, val2: ISimpleListItem): boolean {
    return val1?.id?.toString() === val2?.id?.toString();
  }

  public override onAllChecked(value: boolean): void {
    this.data.results.forEach(item => this.updateCheckedSet(item.articleVariant.id, value));
    this.refreshCheckedStatus();
    this._cdr.detectChanges();
  }

  public override onItemChecked(id: number, checked: boolean): void {
    super.onItemChecked(id, checked);
    this._cdr.detectChanges();
  }

  protected override refreshCheckedStatus(): void {
    const rows = this.data.results || [];
    this.checked = rows.length > 0 && rows.every(item => this.setOfCheckedId.has(item.articleVariant.id));
    this.indeterminate = rows.some(item => this.setOfCheckedId.has(item.articleVariant.id)) && !this.checked;
  }

  public createOrderFromSelection(): void {
    const selected = this.data.results.filter(item => this.setOfCheckedId.has(item.articleVariant.id));
    if (selected.length === 0) {
      return;
    }

    const withoutSupplier = selected.filter(item => !item.supplier?.id);
    if (withoutSupplier.length > 0) {
      this._notifyService.pushError('Fehler', 'Die ausgewählten Artikel haben keinen Lieferanten.');
      return;
    }

    const supplierIds = new Set(selected.map(item => item.supplier.id));
    if (supplierIds.size > 1) {
      this._notifyService.pushError('Fehler', 'Bitte nur Artikel desselben Lieferanten auswählen.');
      return;
    }

    const supplier = selected[0].supplier;
    const supplierOption: ISimpleListItem = {
      id: String(supplier.id),
      value: supplier.name,
    };
    const orderProducts = selected.map(item => this.toOrderProduct(item));
    const draft = {
      supplier: {
        ...supplier,
        ...supplierOption,
      },
      orderDate: new Date(),
      orderType: OrderTypes.NonPacking,
      orderProducts,
      orderDocumentKeys: [],
      isPaid: false,
      isInvoiceUploaded: false,
    } as unknown as IDeliveryOrder;

    this.addEditOrderModal.showCreateFromArticles(draft);
  }

  public onOrderCreated(): void {
    this.setOfCheckedId.clear();
    this.refreshCheckedStatus();
    this.onRefreshList(false);
  }

  private toOrderProduct(article: INonPackableArticle): IDeliveryOrderProduct {
    const variant = article.articleVariant;
    const name = `${variant.articleName || ''} ${variant.name || ''}`.trim();

    return {
      articleId: variant.articleId,
      articleVariantId: variant.id,
      name,
      number: variant.orderNumber,
      quantity: 1,
      status: 'Open',
      checked: false,
      isBagable: false,
      product: String(variant.id),
    } as unknown as IDeliveryOrderProduct;
  }
}
